import { useMemo } from "react"
import type { Price } from "@stockline/shared/types/pricing/price"
import { PRICE_TABLE } from "../../../lib/offline/resources/pricing/price/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

export interface UsePricesFilter {
  productId?: string
  clientGroupId?: string
  /**
   * When set, returns the user-level override rows for that salesperson
   * IN ADDITION TO the company-level rows (userId IS NULL). Recap call
   * sites filter further in render code.
   *
   * When unset, returns every row in the cache — typical for the
   * full-store joins the effective-price resolver runs over.
   */
  userId?: string
}

/**
 * Reactive view of client-group product prices for the current company.
 *
 * Mirrors the stocks/locations pattern: backed by the SyncEngine-managed
 * Dexie store, auto-updates on any `price.{created,updated,deleted}`
 * SSE event without page reload. The frontend's effective-price
 * resolver (recap surfaces) consumes this alongside `usePartners()` and
 * `useClientGroups()` to pick the right override per (partner, product)
 * pair.
 */
/**
 * "User-level override + company-level fallback" filter — exported so
 * tests can pin the OR-with-NULL semantics (the P1 #1281 regression
 * was a missing OR branch). Inlining the filter inside `usePrices`
 * leaves no test surface for the predicate itself; pulling it out as
 * a named function gives test code a stable target.
 */
export function filterPricesByUserId<T extends { userId: string | null }>(
  rows: T[],
  userId: string | undefined
): T[] {
  if (userId === undefined) return rows
  return rows.filter((row) => row.userId === userId || row.userId === null)
}

export function usePrices(filter?: UsePricesFilter): Price[] {
  const { reactive } = useAISync()
  // Only forward productId/clientGroupId to the adapter — passing
  // `userId` to the exact-match Dexie filter would silently drop
  // company-level rows (`userId === null`). Apply the userId filter
  // in JS instead so the hook honours its documented contract:
  // "user-level override + company-level fallback".
  const rows = reactive.useQuery<Price>(PRICE_TABLE, {
    productId: filter?.productId,
    clientGroupId: filter?.clientGroupId,
    orderBy: "createdAt",
  })
  // Tombstone filter + userId hierarchy in one stable memo. Without
  // this memo, `.filter()` would build a fresh array on every render
  // — `useLiveQuery` keeps `rows` referentially stable between Dexie
  // ticks, so consumers' `useMemo` deps would otherwise re-run on
  // every parent render. Effective-price resolution downstream
  // relies on this hook returning only live rows.
  const userId = filter?.userId
  return useMemo(
    () =>
      filterPricesByUserId(
        rows.filter((r) => !r.deletedAt),
        userId
      ),
    [rows, userId]
  )
}
